import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Box, Button, CssBaseline, Typography } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import SamlSessionComplete from './pages/SamlSessionComplete';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Applications from './pages/Applications';
import IGAOnboarding from './pages/IGAOnboarding';
import DependencyGraph from './pages/DependencyGraph';
import UserProfile from './pages/UserProfile';
import Settings from './pages/Settings';
import Security from './pages/Security';
import Contacts from './pages/Contacts';
import AuditTrail from './components/AuditTrail';
import Reports from './pages/Reports';
import TurboChatPage from './pages/TurboChatPage';
import VysorAgentLabPage from './pages/VysorAgentLabPage';
import RiskQuestionManagement from './pages/RiskQuestionManagement';
import RiskPolicies from './pages/RiskPolicies';
import RiskAssessmentReview from './pages/RiskAssessmentReview';
import SSOReview from './pages/SSOReview';
import PAMDashboard from './pages/PAMDashboard';
import AIGovernance from './pages/AIGovernance';
import Layout from './components/Layout';
import RoleRoute from './components/RoleRoute';
import { isMercuryTenant } from './utils/tenantBranding';
import TenantBrandingHead from './components/TenantBrandingHead';
import TenantBrandingBootstrap from './components/TenantBrandingBootstrap';
import CenteredLoader from './components/CenteredLoader';

// Create LinkedIn-inspired theme (replaced by ThemeContext)
/*
const theme = createTheme({
  palette: {
    primary: {
      main: '#0077B5', // LinkedIn blue
      light: '#00A0DC',
      dark: '#005885',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#004471', // LinkedIn dark blue
      light: '#006097',
      dark: '#002D4A',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F8F9FA',
      paper: '#ffffff',
    },
    text: {
      primary: '#1A1A1A',
      secondary: '#666666',
    },
    grey: {
      50: '#F8F9FA',
      100: '#F1F3F4',
      200: '#E0E0E0',
      300: '#CCCCCC',
      400: '#999999',
      500: '#666666',
      600: '#4D4D4D',
      700: '#333333',
      800: '#1A1A1A',
      900: '#0D0D0D',
    },
    success: {
      main: '#057642',
      light: '#10B981',
      dark: '#065F46',
    },
    warning: {
      main: '#D97706',
      light: '#F59E0B',
      dark: '#B45309',
    },
    error: {
      main: '#DC2626',
      light: '#EF4444',
      dark: '#B91C1C',
    },
    info: {
      main: '#0077B5',
      light: '#00A0DC',
      dark: '#005885',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      color: '#1A1A1A',
    },
    h2: {
      fontWeight: 600,
      color: '#1A1A1A',
    },
    h3: {
      fontWeight: 600,
      color: '#1A1A1A',
    },
    h4: {
      fontWeight: 600,
      color: '#1A1A1A',
    },
    h5: {
      fontWeight: 600,
      color: '#1A1A1A',
    },
    h6: {
      fontWeight: 600,
      color: '#1A1A1A',
    },
    body1: {
      color: '#1A1A1A',
    },
    body2: {
      color: '#666666',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 119, 181, 0.3)',
          },
        },
        contained: {
          backgroundColor: '#0077B5',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#005885',
          },
        },
        outlined: {
          borderColor: '#0077B5',
          color: '#0077B5',
          '&:hover': {
            backgroundColor: 'rgba(0, 119, 181, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          borderRadius: 12,
          border: '1px solid #E0E0E0',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#F8F9FA',
            '&:hover': {
              backgroundColor: '#F1F3F4',
            },
            '&.Mui-focused': {
              backgroundColor: '#ffffff',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0077B5',
                borderWidth: 2,
              },
            },
          },
          '& .MuiInputLabel-root': {
            color: '#666666',
            '&.Mui-focused': {
              color: '#0077B5',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: '#1A1A1A',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          borderRight: '1px solid #E0E0E0',
        },
      },
    },
  },
});
*/

// Protected Route component
const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Box sx={{ p: 4, maxWidth: 560 }}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>This page is not available</Typography>
      <Typography sx={{ mt: 1, mb: 2, color: 'text.secondary' }}>
        That address is not a PROVYSN module. The rest of the app is still here.
      </Typography>
      <Button variant="contained" onClick={() => navigate('/dashboard')}>Go to dashboard</Button>
    </Box>
  );
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <CenteredLoader variant="full" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Default route component that redirects based on user role
const DefaultRoute: React.FC = () => {
  const { user } = useAuth();
  
          // For SSO admin users, redirect to SSO Review
        if (user?.role === 'sso_admin') {
          return <Navigate to="/sso-review" replace />;
        }
  
  // For all other users, redirect to regular Dashboard
  return <Navigate to="/dashboard" replace />;
};

// Main App Routes
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/saml/complete" element={<SamlSessionComplete />} />
      {/* Preview agent UI — full-bleed, not in sidebar. Verify via /vysor-lab */}
      <Route
        path="/vysor-lab"
        element={
          <ProtectedRoute>
            <VysorAgentLabPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DefaultRoute />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="turbochat" element={<TurboChatPage />} />
        <Route
          path="users"
          element={
            <RoleRoute>
              <Users />
            </RoleRoute>
          }
        />
        <Route
          path="applications"
          element={
            <RoleRoute>
              <Applications />
            </RoleRoute>
          }
        />
        <Route
          path="applications/iga-onboarding"
          element={
            <RoleRoute>
              <IGAOnboarding />
            </RoleRoute>
          }
        />
        <Route
          path="dependency-graph"
          element={
            isMercuryTenant() ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <RoleRoute>
                <DependencyGraph />
              </RoleRoute>
            )
          }
        />
        <Route
          path="sso-admin"
          element={<Navigate to="/sso-review" replace />}
        />
        <Route
          path="pam-dashboard"
          element={
            isMercuryTenant() ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <RoleRoute>
                <PAMDashboard />
              </RoleRoute>
            )
          }
        />
        <Route
          path="ai-governance"
          element={
            <RoleRoute>
              <AIGovernance />
            </RoleRoute>
          }
        />
        <Route
          path="reports"
          element={
            <RoleRoute>
              <Reports />
            </RoleRoute>
          }
        />
        <Route path="delegation-management" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="audit-trail"
          element={
            <RoleRoute>
              <AuditTrail />
            </RoleRoute>
          }
        />
        <Route path="user-profile/:userId" element={<UserProfile />} />
        <Route
          path="settings"
          element={
            <RoleRoute>
              <Settings />
            </RoleRoute>
          }
        />
        <Route
          path="security"
          element={
            <RoleRoute>
              <Security />
            </RoleRoute>
          }
        />
        <Route
          path="contacts"
          element={
            <RoleRoute>
              <Contacts />
            </RoleRoute>
          }
        />
        <Route
          path="risk-questions"
          element={
            <RoleRoute>
              <RiskQuestionManagement />
            </RoleRoute>
          }
        />
        <Route
          path="risk-policies"
          element={
            <RoleRoute>
              <RiskPolicies />
            </RoleRoute>
          }
        />
        <Route path="risk-assessment" element={<Navigate to="/applications" replace />} />
        <Route
          path="risk-assessment-review"
          element={
            <RoleRoute>
              <RiskAssessmentReview />
            </RoleRoute>
          }
        />
        <Route
          path="sso-review"
          element={
            <RoleRoute>
              <SSOReview />
            </RoleRoute>
          }
        />
        <Route path="preview/command-center" element={<Navigate to="/dashboard" replace />} />
        <Route path="preview/command-center/*" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <CssBaseline />
      <AuthProvider>
        <NotificationProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <TenantBrandingBootstrap>
              <TenantBrandingHead />
              <AppRoutes />
            </TenantBrandingBootstrap>
          </Router>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
